<template>
    <orders :params="params" v-slot="{ orderList, isLoading, meta: { pagination } = {} }" ref="orders">
        <div class="orders">
            <div>
                <div class="row">
                    <div class="col-sm-6">
                        <div class="form-group">
                            <label>Keywords</label>
                            <input class="form-control" type="text" v-model="orderFilters.keyword">
                        </div>
                    </div>
                    <div class="col-sm-6">
                        <div class="form-group">
                            <label>Dealer</label>
                            <companies v-slot="{ companies }" v-if="isAdministrator">
                                <select class="form-control" v-model="orderFilters.dealer">
                                    <option value="">All Dealers</option>
                                    <option v-for="company in companies" :key="company.id" :value="company.id" v-text="company.name"></option>
                                </select>
                            </companies>
                            <div v-else>
                                {{ dealerName }}
                            </div>
                        </div>
                    </div>
                </div>
                <div class="row">
                    <div class="col-sm-2">
                        <div class="form-group mb-sm-0">
                            <label>Include Batched Orders</label>
                            <select class="form-control" v-model="orderFilters.batched">
                                <option value="">All</option>
                                <option value="yes">Batched Only</option>
                                <option value="no">Unbatched Only</option>
                            </select>
                        </div>
                    </div>
                    <div class="col-sm-2">
                        <div class="form-group mb-sm-0">
                            <label>Ordered On</label>
                            <datepicker v-model="orderFilters.order_date" format="MMM-d-yyyy" :clear-button="true"></datepicker>
                        </div>
                    </div>
                    <div class="col-sm-2">
                        <div class="form-group mb-sm-0">
                            <label>Delivered On</label>
                            <datepicker v-model="orderFilters.delivery_date" format="MMM-d-yyyy" :clear-button="true"></datepicker>
                        </div>
                    </div>
                    <div class="col-sm-2">
                        <div class="form-group mb-sm-0">
                            <label>Order Status</label>
                            <select class="form-control" v-model="orderFilters.status">
                                <option value="">All Orders</option>
                                <option v-for="status in validStatuses(orderList)" v-bind:value="status" v-text="status"></option>
                            </select>
                        </div>
                    </div>
                    <div class="col-sm-2">
                        <div class="form-group mb-sm-0">
                            <label>Sort by Due Date</label>
                            <select class="form-control" v-model="orderFilters.sort_direction">
                                <option value="">None</option>
                                <option value="desc">Descending</option>
                                <option value="asc">Ascending</option>
                            </select>
                        </div>
                    </div>
                    <div class="col-sm-2 d-sm-flex align-items-sm-end justify-content-end">
                        <input class="btn btn-primary flex-grow-1" @click="resetFilters" type="submit" value="Reset" />
                        <input class="btn btn-primary ml-2 flex-grow-1" @click="submitFilters" type="submit" value="Filter" />
                    </div>
                </div>
            </div> 

            <div v-if="pagination" class="meta header" v-text="filterSummary(pagination)"></div>

            <div v-if="successMessage" class="alert alert-success">{{ successMessage }}</div>
            <div v-if="errors && errors.length" class="alert alert-danger">
                <ul>
                    <li v-for="error in errors" v-text="Object.values(error).join()"></li>
                </ul>
            </div>

            <section class="table-responsive">
                <table class="table table-striped orders-table manager-list">
                    <thead class="manager-list-heading headings">
                        <tr>
                            <th></th>
                            <th class="order-id">ID &amp; Batch #</th>
                            <th class="order-date">Due Date</th>

                            <th v-if="isDealer" class="customer">Customer</th>
                            <th v-else class="customer">Customer &amp; Dealer</th>

                            <th class="price">Order</th>
                            <th class="delivered">Fees</th>
                            <th class="status">Status</th>
                        </tr>
                    </thead>
                    <tbody v-if="orderList && !isLoading">
                        <order-row v-for="order in orderList" :id="order.id" :key="order.id" :dealer="isDealer"
                            @edit="editOrder"
                            @pending="addRowToPending"
                            @clear="removeRowFromPending" />
                    </tbody>
                    <tbody v-else>
                        <tr>
                            <td colspan="7" class="text-center">
                                <div class="spinner-border" role="status">
                                    <span class="sr-only">Loading...</span>
                                </div>
                                <p>Loading...</p>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </section>

            <div class="footer">
                <div class="paging d-flex flex-wrap justify-content-between">
                    <button v-if="hasPendingRows" class="btn btn-primary d-block mb-3 mx-auto" @click="saveAll">Save all changes</button>
                    <pagination
                        v-if="pagination"
                        v-model="orderFilters.page"
                        :total-pages="pagination.last_page"
                        :value="pagination.current_page"
                    >
                    </pagination>
                </div>
            </div>

            <div v-if="successMessage" class="alert alert-success">{{ successMessage }}</div>
            <div v-if="errors && errors.length" class="alert alert-danger">
                <ul>
                    <li v-for="error in errors" v-text="Object.values(error).join()"></li>
                </ul>
            </div>
        </div>
    </orders>
</template>
<script>
import OrderRow from './OrderRow';
import OrderDetail from './OrderDetail';
import Datepicker from 'vuejs-datepicker';
import moment from 'moment';
import { ContinuousCollectionComponent, PaginatedCollectionComponent, PaginationNav, SyncsWithUrl } from 'clockvine-vue';
import Filtering from './helpers/Filtering';
import {sessionSaveState} from '@khamer/vue-save-state';

export default {
    mixins: [
        sessionSaveState,
        Filtering,
        new SyncsWithUrl("orderFilters", {
            status: '',
            page: 1,
            order_date: '',
            delivery_date: '',
            keyword: '',
            dealer: '',
            from: '',
            through: '',
            batched: '',
            sort_direction: '',
            refresh: null
        })
    ],

    components: {
        OrderDetail,
        OrderRow,
        Datepicker,
        pagination: PaginationNav,
        companies: ContinuousCollectionComponent.for("companies"),
        orders: PaginatedCollectionComponent.for("orderList")
    },

    data: () => ({
        modalOrder: null,
        pendingRows: {},
        errors: {},
        successMessage: '',
        orderFilters: {
            status: '',
            page: 1,
            order_date: '',
            delivery_date: '',
            keyword: '',
            dealer: '',
            from: '',
            through: '',
            batched: '',
            sort_direction: '',
            refresh: null,
        },
        params: {},
        user: {
            company:null,
            role:'Dealer',
        },
        isAdministrator: false,
    }),

    computed: {
        hasPendingRows() {
            return this.pendingRows && Object.keys(this.pendingRows).length;
        },

        isDealer() {
            return !!this.user.company;
        },

        dealerName() {
            if (this.user.company) {
                let dealers = JSON.parse(JSON.stringify(this.$store.state.companies.elements));
                if (this.user.company in dealers) {
                    return dealers[this.user.company].name;
                }
            }

            return '';
        },

        hasNextPage() {
            return this.$store.getters['orderList/nextPage'];
        },

        hasPrevPage() {
            return this.$store.getters['orderList/prevPage'];
        },

        pageSummary() {
            return this.$store.getters['orderList/pageSummary'];
        },
    },

    created() {
        this.params = JSON.parse(JSON.stringify(this.orderFilters));
        this.$store.dispatch('companies/index');
        this.$store.dispatch('orderList/index');

        var message = this.$root.getFlashMessage('orderSuccess', true);
        if (!message) {
            message = localStorage.getItem('orderSuccess');
            localStorage.removeItem('orderSuccess');
        }

        this.setSuccess(message);
    },

    mounted() {
        this.user = JSON.parse(this.$root.$el.attributes['data-user'].value);
        this.isAdministrator = (this.user.role == 'Administrator');

        // Run a bit of jquery to stick the table footer
        this.$nextTick(() => {
            let $tableFooter = $('.orders .footer');
            let $dupe = $tableFooter.clone().css({visibility: 'hidden'}).insertAfter($tableFooter);
            $tableFooter.css({position: 'absolute'});

            var updateFooter = function(forceFix) {
                var bottomEdge = $(window).scrollTop() + $(window).height();

                if ($dupe.offset() === undefined) {
                    return;
                }

                if (forceFix === true || bottomEdge - $dupe.offset().top - $tableFooter.height() < 0) {
                    $tableFooter.css({
                        position: 'fixed',
                        width: $dupe.width(),
                        boxShadow: '0 -5px 10px -5px rgba(0,0,0,.25)',
                        bottom: 0,
                    });
                } else {
                    $tableFooter.css({
                        position: 'absolute',
                        width: $dupe.width(),
                        boxShadow: 'none',
                        bottom: '',
                    });
                }
            };
            $(window).on('scroll resize', updateFooter);
            updateFooter(true);
        });
        this.submitFilters();
    },

    methods: {
        validStatuses(orders) {
            if (orders && orders.length && orders[0].valid_statuses) {
                return orders[0].valid_statuses;
            }

            return ['New', 'Processing', 'Delivered', 'Canceled'];
        },

        nextPage() {
            this.$store.dispatch('orderList/index', {url: this.$store.state.orderList.next_page_url})
                .then(response => {
                    this.orderFilters.page = response.data.data.current_page;
                });
        },

        prevPage() {
            this.$store.dispatch('orderList/index', {url: this.$store.state.orderList.prev_page_url})
                .then(response => {
                    this.orderFilters.page = response.data.data.current_page;
                });
        },

        resetFilters() {
            this.orderFilters = {
                status: '',
                page: 1,
                order_date: '',
                delivery_date: '',
                keyword: '',
                dealer: '',
                from: '',
                through: '',
                batched: '',
                sort_direction: '',
                refresh: null
            };
            return this.submitFilters();
        },

        submitFilters(mustGet = true) {
            this.orderFilters.page = 1;
            this.$refs.orders.query({ mustGet: mustGet })
                .then(() => {
                    this.params = JSON.parse(JSON.stringify(this.orderFilters));
                    this.$forceUpdate();
                })
                .catch(errors => {
                    console.log(errors);
                });
        },

        editOrder(id) {
            this.modalOrder = id;
        },

        addRowToPending(element) {
            this.$set(this.pendingRows, element.id, element);
        },

        removeRowFromPending(element) {
            this.$delete(this.pendingRows, element.id);
        },

        saveAll() {
            let saves = [];
            let successfullySaved = [];
            let didntSave = [];
            let tableComponent = this;
            tableComponent.errors = [];

            Object.values(this.pendingRows).forEach(element => {

                saves.push(
                    element.$update()
                        .then(() => {
                            successfullySaved.push(element);
                        })
                        .catch(errors => {
                            didntSave.push(element);

                            errors.response.data.data.errors.map(obj => {
                                for (let field in obj) {
                                    obj[field] = `Order #${element.id}: ${obj[field]}`;
                                }
                                return obj;
                            });

                            tableComponent.errors = tableComponent.errors.concat(errors.response.data.data.errors);
                        })

                );
            });

            Promise.all(saves).then(() => {
                let msg = '';

                if (successfullySaved.length > 1) {
                    msg = 'Successfully saved changes to Orders ' + successfullySaved.map(r => '#' + r.id).join(', ') + '.';
                } else if (successfullySaved.length == 1) {
                    msg = 'Successfully save changes to Order #' + successfullySaved[0].id + '.';
                }

                if (msg) {
                    this.setSuccess(msg);
                }

                this.pendingRows = {};
                this.$refs.orders.query({ mustGet: true });
                this.submitFilters(false);
            });

        },

        setSuccess(message) {
            this.successMessage = message;

            if (message) {
                var parentScope = this;
                setTimeout(function() { parentScope.successMessage = ''; }, 6000);
            }

        },
        getSaveStateConfig() {
            return {
                'cacheKey': 'orderFilters',
                'saveProperties': ['orderFilters'],
            };
        },
    },

    watch: {
        'orderFilters.refresh': function(to, from) {
            if (to) {
                this.$refs.orders.query({mustGet: true});
                this.submitFilters();
                this.orderFilters.refresh = null;
            }
        },
        'orderFilters.page': function(to, from) {
            if (to != from) {
                this.params.page = to;
            }
        },
    },

    beforeRouteLeave(to, from, next) {
        from.meta.filters = JSON.parse(JSON.stringify(this.orderFilters));
        next();
    },

    beforeRouteEnter(to, from, next) {
        next(vm => {
            if (to.meta.filters) {
                for (let [field, val] of Object.entries(to.meta.filters)) {
                    vm.$set(vm.orderFilters, field, val);
                }
            }
        });
    },

}
</script>
